import { Component } from "react";
import UserAuthenticationLayout from "./UserAuthenticationLayout";
import { connect } from "react-redux";
import {
  emailAuthenticationVerificationRequtestAsync,
  phoneAuthenticationVerificationRequtestAsync,
  requestForEmailSignInAsync,
  requestForPhoneSignInAsync,
} from "store/slices/AuthenticationSlice";
import { withRouter } from "react-router-dom";
import { AppDispatch, RootState } from "store/store";
import { unwrapResult } from "@reduxjs/toolkit";
import { History } from "history";
import { SignInSignUpOption } from "shared/enums/SignInSignUpOptionEnum";
import Loader from "components/common/controls/Loader";
import SignInForm, { SignInFormData } from "./SignInForm";
import PromineoStepWizard from "components/common/PromineoStepWizard";
import VerificationCodeProvider from "fetaures/verification/VerificationCodeProvider";
import EmailAuthenticationVerificationRequest from "interfaces/request/EmailAuthenticationVerificationRequest";
import VerificationResponse from "interfaces/response/VerificationResponse";
import PhoneAuthenticationVerificationRequest from "interfaces/request/PhoneAuthenticationVerificationRequest";
import EmailSignInRequest from "interfaces/request/EmailSignInRequest";
import PhoneSignInRequest from "interfaces/request/PhoneSignInRequest";

enum Steps {
  SIGNIN_FORM = 1,
  VERIFICATION_CODE_FORM = 2,
}

interface Props {
  dispatch?: AppDispatch;
  history?: History;
  isLoading: boolean;
}

interface State {
  signInFormData: SignInFormData;
  verificationResponse: VerificationResponse;
  stepWizard?: any;
}

export class SignIn extends Component<Props, State> {
  state: Readonly<State> = {
    signInFormData: {
      authenticationType: SignInSignUpOption.Email,
      email: "",
      phone: "",
      phoneCode: "",
      phoneNumberWithCode: "",
    },
    verificationResponse: {
      receiverAddress: "",
      expiryTime: "",
      token: "",
      code: "",
    },
  };

  setStepWizardInstance = (instance: any) => {
    this.setState({
      stepWizard: instance,
    });
  };

  goToVerificationStep = () => {
    this.state.stepWizard?.goToStep(Steps.VERIFICATION_CODE_FORM);
  };

  goToSignInStep = () => {
    this.state.stepWizard?.goToStep(Steps.SIGNIN_FORM);
  };

  handleAuthenticationVerificationResponse = (verificationResponse: any) => {
    try {
      const response: VerificationResponse = unwrapResult(verificationResponse);
      this.setState(
        {
          verificationResponse: response,
        },
        () => {
          this.goToVerificationStep();
        }
      );
    } finally {
    }
  };

  handleEmailAuthenticationVerification = () => {
    const verificationRequest: EmailAuthenticationVerificationRequest = {
      email: this.state.signInFormData.email,
    };

    this.props
      .dispatch?.(
        emailAuthenticationVerificationRequtestAsync(verificationRequest)
      )
      .then(this.handleAuthenticationVerificationResponse);
  };

  handlePhoneAuthenticationVerification = () => {
    const verificationRequest: PhoneAuthenticationVerificationRequest = {
      phone: this.state.signInFormData.phone,
      phoneCode: this.state.signInFormData.phoneCode,
    };

    this.props
      .dispatch?.(
        phoneAuthenticationVerificationRequtestAsync(verificationRequest)
      )
      .then(this.handleAuthenticationVerificationResponse);
  };

  requestForVerificationCode = () => {
    if (
      this.state.signInFormData.authenticationType === SignInSignUpOption.Email
    ) {
      this.handleEmailAuthenticationVerification();
    } else if (
      this.state.signInFormData.authenticationType === SignInSignUpOption.Phone
    ) {
      this.handlePhoneAuthenticationVerification();
    }
  };

  handleSignInFormSubmit = (signInFormData: SignInFormData) => {
    this.setState(
      {
        signInFormData: signInFormData,
      },
      () => {
        this.requestForVerificationCode();
      }
    );
  };

  handleVerificationCodeSubmit = (code: string) => {
    if (
      this.state.signInFormData.authenticationType === SignInSignUpOption.Email
    ) {
      const signInRequest: EmailSignInRequest = {
        code: code,
        token: this.state.verificationResponse.token,
        email: this.state.signInFormData.email,
      };
      this.props.dispatch?.(requestForEmailSignInAsync(signInRequest));
    } else if (
      this.state.signInFormData.authenticationType === SignInSignUpOption.Phone
    ) {
      const signInRequest: PhoneSignInRequest = {
        code: code,
        token: this.state.verificationResponse.token,
        phone: this.state.signInFormData.phone,
        phoneCode: this.state.signInFormData.phoneCode,
      };
      this.props.dispatch?.(requestForPhoneSignInAsync(signInRequest));
    }
  };

  checkIfVerificationTypeIsEmail = () => {
    return (
      this.state.signInFormData.authenticationType === SignInSignUpOption.Email
    );
  };

  render() {
    return (
      <>
        <UserAuthenticationLayout>
          <PromineoStepWizard
            initialStep={Steps.SIGNIN_FORM}
            instance={this.setStepWizardInstance}
          >
            <SignInForm onSubmit={this.handleSignInFormSubmit} />
            <VerificationCodeProvider
              codeSendingAddress={
                this.checkIfVerificationTypeIsEmail()
                  ? this.state.signInFormData.email
                  : this.state.signInFormData.phoneNumberWithCode
              }
              expireTime={this.state.verificationResponse.expiryTime}
              token={this.state.verificationResponse.token}
              authenticationType={
                this.checkIfVerificationTypeIsEmail()
                  ? SignInSignUpOption.Email
                  : SignInSignUpOption.Phone
              }
              backLinkText="back to sign in"
              onBackLinkClick={this.goToSignInStep}
              onVerificationCodeInputComplete={
                this.handleVerificationCodeSubmit
              }
            />
          </PromineoStepWizard>
        </UserAuthenticationLayout>
        <Loader isVisible={this.props.isLoading} />
      </>
    );
  }
}

const mapStateToProps = (state: RootState) => {
  return {
    isLoading: state.authData.loading,
  };
};

export default withRouter(connect(mapStateToProps)(SignIn));
